import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState, useEffect } from "react";
import { 
  Mail, 
  Phone, 
  Building, 
  Clock, 
  User, 
  MessageSquare, 
  CheckCircle, 
  AlertCircle,
  Archive,
  Eye,
  Edit,
  Trash2
} from "lucide-react";

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  subject?: string;
  message: string;
  status: 'nouveau' | 'en_cours' | 'traite' | 'archive';
  priority: 'basse' | 'normale' | 'haute' | 'urgente';
  created_at: string;
  updated_at: string;
  treated_at?: string;
  treated_by?: string;
  notes?: string;
}

const ContactAdmin = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadMessages();
  }, [statusFilter]);

  const loadMessages = async () => {
    setIsLoading(true);
    try {
      console.log('Loading messages with filter:', statusFilter);
      
      // Accès direct à la base de données au lieu de passer par Edge Function
      let query = supabase
        .from('contact_messages_20251219_1800')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;
      
      if (error) {
        console.error('Database query error:', error);
        throw error;
      }
      
      console.log('Messages loaded:', data?.length || 0);
      setMessages(data || []);
    } catch (error: any) {
      toast({
        title: "Erreur de chargement",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateMessageStatus = async (messageId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('contact_messages_20251219_1800')
        .update({ 
          status: newStatus,
          treated_at: newStatus === 'traite' ? new Date().toISOString() : null,
          treated_by: newStatus === 'traite' ? 'Admin' : null
        })
        .eq('id', messageId);

      if (error) throw error;

      toast({
        title: "Statut mis à jour",
        description: `Message marqué comme ${getStatusLabel(newStatus)}`,
      });

      loadMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(prev => prev ? { ...prev, status: newStatus as any } : null);
      }
    } catch (error: any) {
      toast({
        title: "Erreur de mise à jour",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const addNotes = async (messageId: string) => {
    if (!notes.trim()) return;

    try {
      const { error } = await supabase
        .from('contact_messages_20251219_1800')
        .update({ notes: notes.trim() })
        .eq('id', messageId);

      if (error) throw error;

      toast({
        title: "Notes ajoutées",
        description: "Les notes ont été sauvegardées",
      });

      setNotes('');
      loadMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(prev => prev ? { ...prev, notes: notes.trim() } : null);
      }
    } catch (error: any) {
      toast({
        title: "Erreur de sauvegarde",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      nouveau: { variant: "destructive" as const, label: "Nouveau", icon: AlertCircle },
      en_cours: { variant: "default" as const, label: "En cours", icon: Clock },
      traite: { variant: "secondary" as const, label: "Traité", icon: CheckCircle },
      archive: { variant: "outline" as const, label: "Archivé", icon: Archive }
    };
    
    const config = variants[status as keyof typeof variants] || variants.nouveau;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const variants = {
      basse: { variant: "outline" as const, label: "Basse" },
      normale: { variant: "secondary" as const, label: "Normale" },
      haute: { variant: "default" as const, label: "Haute" },
      urgente: { variant: "destructive" as const, label: "Urgente" }
    };
    
    const config = variants[priority as keyof typeof variants] || variants.normale;
    
    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      nouveau: "nouveau",
      en_cours: "en cours",
      traite: "traité",
      archive: "archivé"
    };
    return labels[status as keyof typeof labels] || status;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const getMessagesByStatus = () => {
    const counts = {
      nouveau: messages.filter(m => m.status === 'nouveau').length,
      en_cours: messages.filter(m => m.status === 'en_cours').length,
      traite: messages.filter(m => m.status === 'traite').length,
      archive: messages.filter(m => m.status === 'archive').length,
      total: messages.length
    };
    return counts;
  };

  const stats = getMessagesByStatus();

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto space-y-8">
          
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2">Administration Contact</h1>
            <p className="text-muted-foreground">
              Gérez les messages de contact reçus via le site web
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{stats.nouveau}</div>
                <div className="text-sm text-muted-foreground">Nouveaux</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.en_cours}</div>
                <div className="text-sm text-muted-foreground">En cours</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{stats.traite}</div>
                <div className="text-sm text-muted-foreground">Traités</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-gray-600">{stats.archive}</div>
                <div className="text-sm text-muted-foreground">Archivés</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-sm text-muted-foreground">Total</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Messages List */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Messages de contact</h2>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filtrer par statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les messages</SelectItem>
                    <SelectItem value="nouveau">Nouveaux</SelectItem>
                    <SelectItem value="en_cours">En cours</SelectItem>
                    <SelectItem value="traite">Traités</SelectItem>
                    <SelectItem value="archive">Archivés</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isLoading ? (
                <div className="text-center py-8">Chargement...</div>
              ) : messages.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Aucun message trouvé</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {messages.map((message) => (
                    <Card 
                      key={message.id} 
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        selectedMessage?.id === message.id ? 'ring-2 ring-primary' : ''
                      }`}
                      onClick={() => setSelectedMessage(message)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{message.name}</span>
                            {message.company && (
                              <Badge variant="outline" className="text-xs">
                                {message.company}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(message.status)}
                            {getPriorityBadge(message.priority)}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {message.email}
                          </div>
                          {message.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {message.phone}
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(message.created_at)}
                          </div>
                        </div>
                        
                        {message.subject && (
                          <div className="font-medium text-sm mb-1">{message.subject}</div>
                        )}
                        
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {message.message}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Message Detail */}
            <div className="space-y-4">
              {selectedMessage ? (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>Détails du message</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedMessage(null)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-medium mb-2">Informations contact</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span>{selectedMessage.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <a href={`mailto:${selectedMessage.email}`} className="text-blue-600 hover:underline">
                              {selectedMessage.email}
                            </a>
                          </div>
                          {selectedMessage.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <a href={`tel:${selectedMessage.phone}`} className="text-blue-600 hover:underline">
                                {selectedMessage.phone}
                              </a>
                            </div>
                          )}
                          {selectedMessage.company && (
                            <div className="flex items-center gap-2">
                              <Building className="h-4 w-4 text-muted-foreground" />
                              <span>{selectedMessage.company}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {selectedMessage.subject && (
                        <div>
                          <h4 className="font-medium mb-2">Sujet</h4>
                          <p className="text-sm">{selectedMessage.subject}</p>
                        </div>
                      )}

                      <div>
                        <h4 className="font-medium mb-2">Message</h4>
                        <div className="bg-muted p-3 rounded-lg text-sm whitespace-pre-line">
                          {selectedMessage.message}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-2">Statut</h4>
                        <Select 
                          value={selectedMessage.status} 
                          onValueChange={(value) => updateMessageStatus(selectedMessage.id, value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nouveau">Nouveau</SelectItem>
                            <SelectItem value="en_cours">En cours</SelectItem>
                            <SelectItem value="traite">Traité</SelectItem>
                            <SelectItem value="archive">Archivé</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <h4 className="font-medium mb-2">Notes internes</h4>
                        {selectedMessage.notes && (
                          <div className="bg-yellow-50 p-3 rounded-lg text-sm mb-2">
                            {selectedMessage.notes}
                          </div>
                        )}
                        <div className="space-y-2">
                          <Textarea
                            placeholder="Ajouter une note..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                          />
                          <Button 
                            size="sm" 
                            onClick={() => addNotes(selectedMessage.id)}
                            disabled={!notes.trim()}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Ajouter note
                          </Button>
                        </div>
                      </div>

                      <div className="text-xs text-muted-foreground space-y-1">
                        <div>Reçu le : {formatDate(selectedMessage.created_at)}</div>
                        <div>Mis à jour : {formatDate(selectedMessage.updated_at)}</div>
                        {selectedMessage.treated_at && (
                          <div>Traité le : {formatDate(selectedMessage.treated_at)}</div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      Sélectionnez un message pour voir les détails
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactAdmin;